import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, Navigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronDown, Lock, Share2, X } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSession } from '@/hooks/useSessions'
import { ApiError } from '@/api/client'
import { useStreamingMessage } from '@/hooks/useStreamingChat'
import { useSessionEventStream } from '@/hooks/useSessionEventStream'
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend'
import { usePinToBottom } from '@/hooks/usePinToBottom'

import { useAuthStore } from '@/stores/auth'
import { FROM_CHILD_PARAM, RETURN_PARENT_PARAM, PATIENT_PARAM } from '@/lib/sessionRouting'
import { useRecordChildSummary } from '@/hooks/useSessionHandoff'
import { usePatientDetail, usePatientsConfig } from '@/hooks/usePatients'
import { useActiveShiftAssignments } from '@/hooks/useShifts'
import { useCurrentUser } from '@/hooks/useAuth'
import { formatPatientLabel } from '@/lib/patientLabel'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { MessageList, ChatInput, CitationSheet, StreamingMessageBubble, ChatReadingColumn } from '@/components/chat'
import { PromptInspectorSheet } from '@/components/session/PromptInspectorSheet'
import ShareDialog from '@/components/ShareDialog'
import { ElicitationDialog } from '@/components/chat/agentic'
import { getHumanReadableError } from '@/utils/errorMessages'
import { RequestAccessInterstitial, type RequestAccessStatus } from '@/components/access'
import { getRequestAccess } from '@/lib/requestAccess'
import { createPatientAccessRequest, createSessionAccessRequest } from '@/api/endpoints/access'
import { ALREADY_AUTHORIZED_CODE, REQUEST_ALREADY_PENDING_CODE } from '@/api/endpoints/accessRequests'

import type { CitationResponse } from '@/api/types'
import { useSurface } from '@/hooks/useGuideRegister'
import { ConversationContext } from '@/components/chat/conversationContext'

export default function Session() {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  // A child sub-app appends `?from_child=<childSessionId>` on "Return to chat".
  // When present, the parent (twin) records that child's RETURN VALUE: the
  // server pulls the child's grounded summary and stores it as an
  // inbound_summary message here (see the record mutation below), so the twin
  // reasons over the child's outcome on its next turn.
  //
  // Capture it into state ONCE per parent session, then strip it from the URL
  // (replace, no history entry). Otherwise the param lingers and a later
  // bookmark/notification revisit — or a SECOND run from the same parent —
  // would re-fire a live pull for a stale child. Keyed on `id` so navigating
  // between parents clears a previous parent's value rather than leaking it.
  const [fromChildSessionId, setFromChildSessionId] = useState<string | null>(null)
  useEffect(() => {
    const fromChild = searchParams.get(FROM_CHILD_PARAM)
    setFromChildSessionId(fromChild) // capture-or-clear for this parent
    if (fromChild) {
      const next = new URLSearchParams(searchParams)
      next.delete(FROM_CHILD_PARAM)
      // A return_to return also names the parent explicitly (needed by /home,
      // redundant here — the parent is the route param). Strip it alongside.
      next.delete(RETURN_PARENT_PARAM)
      setSearchParams(next, { replace: true })
    }
    // Intentionally keyed on `id` only: read the param at mount / parent change.
    // Depending on searchParams would re-run after our own strip and clear the
    // captured value. setSearchParams is stable (react-router).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  const navigate = useNavigate()
  const { t } = useTranslation()
  const twinName = useAuthStore((state) => state.user?.twinName)

  // Patient label for the header: patients are nameless — the label is a
  // config-driven positional identifier (e.g. "ICU-3"). Mirrors Home.tsx's
  // label machinery. Personal (unassigned) sessions show nothing extra.
  const labelFormat = usePatientsConfig()?.label_format

  // IMPORTANT: All hooks must be called unconditionally before any early returns
  // to comply with React's Rules of Hooks. Using empty string fallback for hooks
  // that require an ID, then guard with early return after.
  const isNewSession = id === 'new'
  const sessionId = id ?? ''
  const { data: session, isLoading, error, refetch } = useSession(sessionId, {
    enabled: !isNewSession && !!sessionId,
  })

  // Child → parent return value: when we arrive with ?from_child, the parent
  // asks the server to pull that child's grounded summary and record it as an
  // inbound_summary message here, then refetch so it renders in the timeline
  // (and lands in the twin's LLM context). Parent-initiated, server-pulled,
  // idempotent — the child posts nothing. A 404 means the child had no content
  // to summarise yet (graceful — no card). Fired once per returned child.
  // Shared pull mutation (useRecordChildSummary) — Home.tsx records through
  // the same hook when a return lands on the workspace instead of here.
  const recordChildSummary = useRecordChildSummary()
  const recordedChildRef = useRef<string | null>(null)
  useEffect(() => {
    if (!fromChildSessionId || recordedChildRef.current === fromChildSessionId) return
    recordedChildRef.current = fromChildSessionId
    recordChildSummary.mutate({ parentSessionId: sessionId, childSessionId: fromChildSessionId })
    // Once per returned child: `recordChildSummary` is a react-query mutation
    // whose identity changes on every status transition, so depending on it would
    // re-fire the server-side summary pull for a child already recorded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromChildSessionId])

  // SSE streaming — the only message sending path
  const streaming = useStreamingMessage(sessionId)
  useSessionEventStream(isNewSession ? '' : sessionId, useQueryClient())

  // Fabric: the standalone session page is the chat transcript surface.
  useSurface('transcript')

  // Turn catch-up: useSessionEventStream reconciles this session's pending
  // turns on every connection, so a terminal frame missed during a reload or a
  // backgrounded PWA is read back from the messages. No-op on a new session.

  // Lift citation sheet state to avoid N sheet instances
  const [selectedCitation, setSelectedCitation] = useState<CitationResponse | null>(null)

  // Track degraded services from most recent response

  // Share dialog state
  const [shareOpen, setShareOpen] = useState(false)

  // 403 request-access remediation: a locked conversation (a viewer denied a
  // sub-resource / the whole session) turns its dead-end Retry into a one-tap
  // access request the owner decides. Status mirrors the org inline flow.
  const [reqAccessStatus, setReqAccessStatus] = useState<RequestAccessStatus>('idle')
  const requestSessionAccess = useMutation({
    mutationFn: (justification?: string) =>
      createSessionAccessRequest(sessionId, { access_level: 'viewer', justification }),
    onMutate: () => setReqAccessStatus('pending'),
    onSuccess: () => setReqAccessStatus('sent'),
    onError: (err) => {
      if (err instanceof ApiError && err.code === REQUEST_ALREADY_PENDING_CODE) {
        setReqAccessStatus('already_pending')
      } else if (err instanceof ApiError && err.code === ALREADY_AUTHORIZED_CODE) {
        setReqAccessStatus('already_authorized')
      } else {
        setReqAccessStatus('error')
      }
    },
  })

  // In-session patient tile (viewer without demographics): request the
  // demographics tier, leak-safe via the session linkage. Any precondition
  // failure comes back as a uniform 404 (indistinguishable from a nonexistent
  // patient), so a spurious request degrades safely.
  const [demogStatus, setDemogStatus] = useState<RequestAccessStatus>('idle')
  const requestDemographics = useMutation({
    mutationFn: (patientId: string) =>
      createPatientAccessRequest(patientId, { session_id: sessionId }),
    onMutate: () => setDemogStatus('pending'),
    onSuccess: () => setDemogStatus('sent'),
    onError: (err) => {
      if (err instanceof ApiError && err.code === REQUEST_ALREADY_PENDING_CODE) {
        setDemogStatus('already_pending')
      } else if (err instanceof ApiError && err.code === ALREADY_AUTHORIZED_CODE) {
        setDemogStatus('already_authorized')
      } else {
        setDemogStatus('error')
      }
    },
  })

  // Scroll-position tracking + pin-to-bottom for the chat container. Shared
  // with /onboarding via usePinToBottom — the policy (follow every streaming
  // chunk, stand down only once the user scrolls up herself to re-read) is
  // identical on both surfaces, and when it lived only here the onboarding
  // interview streamed off-screen for want of it.
  const {
    containerRef: scrollContainerRef,
    isPinned,
    scrollToBottom,
  } = usePinToBottom<HTMLElement>()

  // ----- patient frame ----------------------------------------------------
  // A label resolver shared by the existing-session header and the new-session
  // chooser: patients are nameless, so the label is a stable codename derived
  // from the token itself (formatPatientLabel ignores `attributes`/label_format
  // whenever a token is passed — see lib/patientLabel.ts), so this never needs
  // to look the patient up in a list.
  const patientLabel = useCallback(
    (patientId: string) => formatPatientLabel({}, labelFormat, patientId),
    [labelFormat],
  )

  // EXISTING session: its assigned patient (null = personal), used for the
  // header label and to carry the frame into a follow-up conversation.
  const assignedPatientId = session?.session.subject?.type === 'patient' ? session.session.subject.id : null
  const assignedPatientLabel = assignedPatientId ? patientLabel(assignedPatientId) : null

  // Demographics lock: GET /patients/{id} is ACL-gated and returns a uniform
  // 404 for BOTH "doesn't exist" and "you can't see it" (existence-non-leaking,
  // api-service-go HandleGetPatient) — the authoritative signal. The OLD
  // heuristic ("token absent from usePatients()'s list") false-positived for
  // any session bound to a patient the viewer CAN see but who has since been
  // discharged (usePatients() defaults to in_progress-only). Held until the
  // fetch settles so no lock flashes during load; only a 404 counts as locked
  // — a transient network/5xx error must not show a spurious "request access"
  // affordance.
  const {
    data: assignedPatientDetail,
    isLoading: isAssignedPatientLoading,
    error: assignedPatientError,
  } = usePatientDetail(assignedPatientId ?? undefined)
  const patientLocked =
    !!assignedPatientId &&
    !isAssignedPatientLoading &&
    !assignedPatientDetail &&
    assignedPatientError instanceof ApiError &&
    assignedPatientError.status === 404

  // NEW session: the selected patient frame is component state, seeded from the
  // `?patient=<token>` param ('' → none). The "My patients" per-patient button
  // and an in-session follow-up both arrive with this param. Held in state (not
  // read straight off the URL) so the nurse can deselect it or pick a shift
  // patient before sending the first message.
  const paramPatientId = isNewSession ? searchParams.get(PATIENT_PARAM) || null : null
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(paramPatientId)

  // React-router reuses this component instance across `/sessions/:id` changes,
  // so the useState seed above only runs on the very first mount. Re-seed the
  // selection (and re-arm the auto-preselect guard) whenever the route
  // TRANSITIONS into a new-session screen (a follow-up FAB tap navigates to
  // `/sessions/new?patient=X` — `id` changes) OR the patient param itself
  // changes while already there (`/sessions/new?patient=A` → `?patient=B`,
  // same `id` — external link/bookmark; unreachable via in-app flows, but the
  // frame must not stay stale on A). Keyed on the DERIVED paramPatientId, not
  // the searchParams object, so the nurse's manual select/deselect (state
  // only, no URL write) never re-triggers it.
  const didAutoPreselect = useRef(false)
  useEffect(() => {
    if (!isNewSession) return
    setSelectedPatientId(paramPatientId)
    didAutoPreselect.current = false
  }, [id, isNewSession, paramPatientId])

  // Active-shift patients drive the auto-frame: with no explicit param, the
  // nurse's own current patient(s). Mirrors Home.tsx — force-scoped to the
  // caller server-side; `enabled: !!meId` holds the query until the id is known.
  const meId = useCurrentUser()?.id
  const {
    patientIds: activePatientIds,
    isLoading: isShiftsLoading,
    isError: isShiftsError,
    refetch: refetchShifts,
  } = useActiveShiftAssignments({ enabled: isNewSession && !!meId })

  // Patient ids in the nurse's open shift, newest-first (the hook already
  // de-dupes and orders), so a preselect / chip order is stable and meaningful.
  const shiftPatientIds = useMemo(
    () => (isNewSession ? activePatientIds : []),
    [isNewSession, activePatientIds],
  )

  // Auto-preselect EXACTLY ONE shift patient when the nurse arrived without an
  // explicit param and hasn't already touched the selection. This is Joël's
  // "show my patient when I'm on shift". Multiple shift patients are NOT
  // auto-selected — the nurse picks from chips (or leaves it personal). Runs
  // once the shift fetch settles; the `didAutoPreselect` guard (re-armed on each
  // new-session transition above) ensures a later manual deselect sticks.
  useEffect(() => {
    if (!isNewSession || didAutoPreselect.current) return
    if (paramPatientId) {
      didAutoPreselect.current = true
      return
    }
    if (isShiftsLoading) return // wait — no flash of a wrong frame
    didAutoPreselect.current = true
    if (shiftPatientIds.length === 1) setSelectedPatientId(shiftPatientIds[0])
  }, [isNewSession, paramPatientId, isShiftsLoading, shiftPatientIds])

  // Shift patients the nurse can pick from but hasn't (the multi-shift chooser).
  const selectableShiftPatientIds = useMemo(
    () => shiftPatientIds.filter((pid) => pid !== selectedPatientId),
    [shiftPatientIds, selectedPatientId],
  )

  // Deferred session creation: minted on the FIRST message, not eagerly.
  // useDeferredFirstSend owns the whole lifecycle (create → stash → flush) —
  // Home's agent FACE and every board wrapper's side-channel share this same
  // hook, so there is one implementation, not a parallel minting path here.
  // `sessionId: null` while new (not the literal 'new' route param) is what
  // marks this instance unbound; navigate() below is for URL correctness
  // only — the flush itself is keyed on the id THIS hook is bound to, not on
  // the route (see the hook's module doc for why that distinction matters).
  const deferred = useDeferredFirstSend({
    sessionId: isNewSession ? null : sessionId,
    // The nurse-chosen frame (seeded from ?patient= or an active shift,
    // deselectable). `|| undefined` so a cleared selection sends no
    // patient_id rather than "".
    patientId: selectedPatientId || undefined,
    sendMessage: streaming.sendMessage,
    onCreated: (newSession) => void navigate(`/sessions/${newSession.id}`, { replace: true }),
  })

  const handleSend = useCallback(async (content: string) => {
    try {
      await deferred.send(content)
    } catch {
      // Error handled by streaming hook / useDeferredFirstSend
    }
  }, [deferred])

  // Detect stale streaming errors: if the assistant message from the streaming
  // hook is already present in the session's message list, the stream completed
  // successfully and any lingering error state (e.g. Safari "TypeError: Load
  // failed" from a background refetch race) is stale and should not be shown.
  const streamingMessageAlreadyRendered = useMemo(() => {
    if (!streaming.assistantMessageId || !session?.messages) return false
    return session.messages.items.some((m) => m.id === streaming.assistantMessageId)
  }, [streaming.assistantMessageId, session?.messages])

  // Auto-reset streaming state when the final message appears in the list.
  // This clears stale error/content so the StreamingMessageBubble disappears.
  useEffect(() => {
    if (streamingMessageAlreadyRendered && !streaming.isStreaming) {
      streaming.reset()
    }
  }, [streamingMessageAlreadyRendered, streaming.isStreaming, streaming])

  // Guard against missing ID - redirect to home (AFTER all hooks are called)
  if (!id) {
    return <Navigate to="/home" replace />
  }

  if (error && !isNewSession) {
    // A 403 that carries a request_access remediation is a fork, not a wall:
    // show the interstitial (name what's locked + a one-tap request) instead of
    // a Retry that can never succeed. Non-403 / bare-403 errors keep the
    // generic Retry path below unchanged.
    const remediation = getRequestAccess(error)
    if (remediation) {
      return (
        <div className="min-h-dvh bg-neutral-50 flex items-center justify-center p-4">
          <RequestAccessInterstitial
            resource={{
              type: remediation.resourceType,
              id: remediation.resourceId,
              label: t('access.request.thisResource', 'This conversation'),
            }}
            onRequest={(justification) => requestSessionAccess.mutate(justification)}
            status={reqAccessStatus}
            errorMessage={
              requestSessionAccess.error instanceof ApiError
                ? requestSessionAccess.error.message
                : undefined
            }
          />
        </div>
      )
    }

    // error is typed as ApiError (TError bound in useSession), so .code is
    // statically safe — no instanceof narrowing or any-cast required.
    const errorMessage = error.code
      ? getHumanReadableError(error.code, t('session.loadError'))
      : t('session.loadError')

    return (
      <div className="min-h-dvh bg-neutral-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-sm text-error mb-4">{errorMessage}</p>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => void refetch()}
              className="text-sm text-primary font-medium active:scale-95"
            >
              {t('actions.retry', 'Retry')}
            </button>
            <button
              onClick={() => navigate('/home')}
              className="text-sm text-neutral-500 font-medium active:scale-95"
            >
              {t('common.back')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const isPending = deferred.isPending || streaming.isStreaming

  return (
    <div className="h-dvh bg-neutral-100 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-neutral-50 border-b border-neutral-200/50">
        <div className="px-4 py-3 flex items-start gap-3">
          <button
            onClick={() => navigate('/home')}
            aria-label={t('common.back')}
            className="w-9 h-9 -ml-2 flex items-center justify-center text-primary touch-target shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            {isLoading ? (
              <Skeleton className="h-5 w-48" />
            ) : (
              <>
                <h1 className="text-base font-medium text-neutral-900 truncate">
                  {session?.session.title || t('session.newConversation')}
                </h1>
                {isNewSession ? (
                  <NewSessionPatientFrame
                    selectedPatientId={selectedPatientId}
                    selectableShiftPatientIds={selectableShiftPatientIds}
                    patientLabel={patientLabel}
                    onSelect={setSelectedPatientId}
                    onClear={() => setSelectedPatientId(null)}
                    isShiftsLoading={isShiftsLoading}
                    isShiftsError={isShiftsError}
                    onRetryShifts={() => void refetchShifts()}
                  />
                ) : assignedPatientLabel ? (
                  /* Patient frame for an assigned session — the positional label
                     (e.g. "ICU-3"), never a name. When the viewer lacks the
                     demographics tier (patient not in her list), the label is the
                     id tail and a one-tap request sits beside it. Personal
                     sessions show the twin name (if any) instead. */
                  patientLocked ? (
                    <div className="flex items-center gap-1.5">
                      <p className="min-w-0 truncate text-xs font-medium text-neutral-600">
                        {assignedPatientLabel}
                      </p>
                      {demogStatus === 'sent' || demogStatus === 'already_pending' ? (
                        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-tone-ok">
                          <Lock className="h-3 w-3" aria-hidden="true" />
                          {t('access.request.sent', 'Request sent')}
                        </span>
                      ) : demogStatus === 'already_authorized' ? null : (
                        <button
                          type="button"
                          onClick={() =>
                            assignedPatientId && requestDemographics.mutate(assignedPatientId)
                          }
                          disabled={demogStatus === 'pending'}
                          className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-full px-1.5 text-[11px] font-semibold text-primary transition active:scale-95 disabled:opacity-50"
                        >
                          <Lock className="h-3 w-3" aria-hidden="true" />
                          {demogStatus === 'pending'
                            ? t('access.request.sending', 'Sending…')
                            : t('access.patient.requestDemographics', 'Request details')}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs font-medium text-neutral-600 truncate">
                      {assignedPatientLabel}
                    </p>
                  )
                ) : (
                  twinName && <p className="text-xs text-neutral-500">{twinName}</p>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!isNewSession && (
              <>
                {/* Opt-in transparency: see the system prompt, per layer.
                    Subtle ghost icon; the drawer defaults closed and adds no
                    layout shift. A nurse who never taps it never sees it. */}
                <PromptInspectorSheet sessionId={sessionId} />
                <button
                  onClick={() => setShareOpen(true)}
                  aria-label={t('session.share', 'Share conversation')}
                  className="w-9 h-9 flex items-center justify-center text-primary hover:bg-primary/10 rounded-full transition-colors min-w-[44px] min-h-[44px]"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Share dialog */}
      {!isNewSession && sessionId && (
        <ShareDialog
          resourceId={sessionId}
          resourceTitle={session?.session.title || t('session.newConversation')}
          resourceType="session"
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}

      {/* MCP elicitation dialog — blocks stream until user responds */}
      <ElicitationDialog />

      {/* Messages — wrapped in the shared reading column (see
          ChatReadingColumn for why the width lives there). Children
          (MessageList, ProtocolRunner cards, DECISION nodes) inherit it, so
          the page renders as one coherent column rather than ad-hoc
          per-component widths. */}
      <main ref={scrollContainerRef} className="flex-1 overflow-y-auto relative">
        <ConversationContext.Provider value={isNewSession ? null : sessionId}>
          <ChatReadingColumn className="py-4">
              <MessageList
                messages={session?.messages.items ?? []}
                isLoading={isLoading}
                isPending={false}
                emptyMessage={t('session.empty')}
                onCitationClick={setSelectedCitation}
                onRetry={streaming.retry}
                isRetrying={streaming.isRetrying}
              />
              {/* Child return value: while the server pulls + records the child's
                  summary, show progressive feedback. On success it renders as a
                  plain agent message (content_type=text) in the list above (no
                  separate card). An empty child (404 "nothing to summarise") is
                  swallowed at the API binding (recordChildSummary) → it resolves,
                  never reaching the error state below. */}
              {recordChildSummary.isPending && (
                <div className="mt-3 flex items-center gap-2 text-sm text-neutral-500" aria-busy="true">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  {t('chat.inboundSummary.receiving', 'Receiving session summary…')}
                </div>
              )}
              {/* Permanent errors (401/403: no parent write access for a
                  shared-session colleague) render nothing — a Retry would loop
                  forever on a non-transient failure. Only transient errors offer
                  Retry. (The empty-child 404 never reaches here — it resolves at
                  the API binding.) */}
              {recordChildSummary.isError &&
                !(recordChildSummary.error instanceof ApiError &&
                  [401, 403].includes(recordChildSummary.error.status)) && (
                  <div className="mt-3 text-sm text-neutral-700">
                    {t('chat.inboundSummary.loadError', 'Could not load the summary.')}{' '}
                    <button
                      type="button"
                      onClick={() =>
                        fromChildSessionId &&
                        recordChildSummary.mutate({
                          parentSessionId: sessionId,
                          childSessionId: fromChildSessionId,
                        })
                      }
                      className="font-semibold text-primary underline-offset-2 hover:underline active:scale-95"
                    >
                      {t('chat.inboundSummary.retry', 'Retry')}
                    </button>
                  </div>
                )}
              {/* Streaming message bubble - shown during active streaming, retry, or with error.
                  Suppressed when the assistant message is already in the message list (stale error). */}
              {!streamingMessageAlreadyRendered && (streaming.isStreaming || streaming.isRetrying || streaming.error) && (
                <div className="mt-3">
                  <StreamingMessageBubble
                    content={streaming.content}
                    citations={streaming.citations}
                    isStreaming={streaming.isStreaming}
                    error={streaming.error}
                    onRetry={streaming.retry}
                    isRetrying={streaming.isRetrying}
                  />
                </div>
              )}
          </ChatReadingColumn>
        </ConversationContext.Provider>
      </main>

          {/* Input Area — matches the message column width above so the
              input doesn't appear to float outside the conversation.
              The wrapper is the positioning ancestor for the
              scroll-to-bottom pill (absolute positioned at bottom-full,
              i.e. directly above the input). The pill is only rendered
              when the user has scrolled up — at-bottom means no
              affordance needed. */}
          <div className="sticky bottom-0 bg-neutral-50 border-t border-neutral-200/50 pb-safe pb-safe-keyboard">
            {/* Scroll-to-latest pill — anchored above the input (right edge,
                clear of the send button and the keyboard-up layout). Only
                rendered when the nurse has scrolled up HERSELF; a viewport
                still following the conversation needs no affordance, however
                much the page grew or the keyboard resized it. */}
            {!isPinned && (
              <button
                onClick={scrollToBottom}
                aria-label={t('chat.scrollToBottom', 'Scroll to latest')}
                className={cn(
                  'absolute right-4 bottom-full mb-3',
                  'flex items-center gap-1 px-3 py-1.5',
                  'bg-neutral-900 text-white text-xs font-medium rounded-full shadow-lg',
                  'hover:bg-neutral-800 transition-colors',
                )}
              >
                <ChevronDown className="w-4 h-4" />
                {t('chat.scrollToLatest', 'Latest')}
              </button>
            )}
            <ChatReadingColumn className="py-3">
              <ChatInput
                onSend={handleSend}
                isPending={isPending}
                placeholder={t('chat.placeholder')}
              />
            </ChatReadingColumn>
          </div>

      {/* Single citation sheet instance */}
      <CitationSheet
        citation={selectedCitation}
        open={!!selectedCitation}
        onOpenChange={(open) => !open && setSelectedCitation(null)}
      />
    </div>
  )
}

// ===========================================================================
// New-session patient frame — one compact header sub-row that makes the patient
// context VISIBLE and editable before the first message is sent.
//
// States (a new conversation only):
//   - selected      → the patient label as a removable chip (✕ → personal);
//                     a skeleton chip while the patient list resolves (a cold
//                     `?patient=X` load would otherwise flash the raw token).
//   - multi-shift   → the remaining shift patients as selectable chips.
//   - no frame      → a single fact line ("Personal conversation").
//   - shifts loading→ nothing (no flash of a wrong state).
//   - shifts error  → a fact line + Retry (don't assert "personal" when we
//                     couldn't check — but silent nothing is banned too: the
//                     nurse must see that patient context was sought and the
//                     lookup failed, or she sends a personal first message
//                     believing her shift patient is framed).
//
// The label is nameless/positional (formatPatientLabel) — never a PHI name.
// ===========================================================================
function NewSessionPatientFrame({
  selectedPatientId,
  selectableShiftPatientIds,
  patientLabel,
  onSelect,
  onClear,
  isShiftsLoading,
  isShiftsError,
  onRetryShifts,
}: {
  selectedPatientId: string | null
  selectableShiftPatientIds: string[]
  patientLabel: (id: string) => string
  onSelect: (id: string) => void
  onClear: () => void
  isShiftsLoading: boolean
  isShiftsError: boolean
  onRetryShifts: () => void
}) {
  const { t } = useTranslation()

  // A selected patient (from param, auto-preselect, or a chip tap): show it as
  // a removable chip so the nurse can fall back to a personal conversation.
  // `patientLabel` is a pure function of the token (a codename derived
  // client-side, no network round trip) so the label is always immediately
  // available — no loading skeleton needed here.
  if (selectedPatientId) {
    return (
      <div className="mt-0.5 flex items-center gap-1.5">
        <span
          className={cn(
            'inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full',
            'bg-primary/10 text-primary text-xs font-medium max-w-full',
          )}
        >
          <span className="truncate">{patientLabel(selectedPatientId)}</span>
          <button
            type="button"
            onClick={onClear}
            aria-label={t('session.patientFrame.clear')}
            className="shrink-0 w-6 h-6 -my-1 flex items-center justify-center rounded-full text-primary active:scale-95 transition-transform motion-reduce:transition-none"
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </span>
      </div>
    )
  }

  // No selection yet. While shifts load, render nothing rather than a wrong
  // "personal" assertion.
  if (isShiftsLoading) return null

  // Shift lookup failed: state the fact + offer retry. Neither "personal"
  // (false assertion) nor nothing (silent failure) — the nurse must know the
  // patient context couldn't be checked before she frames her first message.
  if (isShiftsError) {
    return (
      <p className="mt-0.5 text-xs text-neutral-500">
        {t('session.patientFrame.shiftsError')}{' '}
        <button
          type="button"
          onClick={onRetryShifts}
          className="font-medium text-primary underline-offset-2 hover:underline active:scale-95"
        >
          {t('actions.retry')}
        </button>
      </p>
    )
  }

  // Active-shift patients to pick from → selectable chips, none preselected.
  if (selectableShiftPatientIds.length > 0) {
    return (
      <div className="mt-1 flex items-center gap-1.5 overflow-x-auto -mx-0.5 px-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {selectableShiftPatientIds.map((pid) => (
          <button
            key={pid}
            type="button"
            onClick={() => onSelect(pid)}
            className={cn(
              'shrink-0 min-h-[28px] px-2.5 py-0.5 rounded-full',
              'border border-neutral-200 bg-white',
              'text-xs font-medium text-neutral-700',
              'active:scale-95 transition-transform motion-reduce:transition-none',
            )}
          >
            {patientLabel(pid)}
          </button>
        ))}
      </div>
    )
  }

  // No param, no active shift → state the fact, minimally.
  return (
    <p className="mt-0.5 text-xs text-neutral-500">
      {t('session.patientFrame.personal')}
    </p>
  )
}
